import { useDrag, useDrop } from 'react-dnd';

import {
  DragCollectedProps,
  DragObject,
  DropCollectedProps,
  UseOrderedDragDropOptions,
  UseOrderedDragDropResult,
} from './useOrderedDragDrop.types';

/** Represents a hook that handles drag/drop ordering of items. */
export const useOrderedDragDrop = ({
  type,
  index,
  source,
  target,
  preview = source,
  onMove,
  canDrag,
  canDrop,
  triggerSwap = 'hover',
}: UseOrderedDragDropOptions): UseOrderedDragDropResult => {
  const [{ isDragging }, connectSource, connectPreview] = useDrag<
    DragObject,
    unknown,
    DragCollectedProps
  >(
    () => ({
      type,
      canDrag,
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [index],
  );

  const [{ isOver }, connectDrop] = useDrop<
    DragObject,
    unknown,
    DropCollectedProps
  >(
    {
      accept: type,
      canDrop,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      [triggerSwap](item, monitor) {
        if (!source.current) {
          return;
        }

        const dragIndex = item.index;
        const hoverIndex = index;

        // Don't replace items with themselves.
        if (dragIndex === hoverIndex) {
          return;
        }

        if (monitor.canDrop()) {
          onMove({ dragIndex, hoverIndex });
        }
      },
    },
    [index, onMove],
  );

  connectPreview(preview);
  connectSource(source);
  connectDrop(target);

  return { isDragging, isOver };
};
